import { useCallback, useRef, type ComponentType } from 'react';
import { useModal, type ModalProps } from '@/contexts/ModalContext';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { MealLogCopyModal } from '~/meal/components/meal-log-copy-modal/MealLogCopyModal';
import { MealLogCopySummaryModal } from '~/meal/components/meal-log-copy-summary-modal/MealLogCopySummaryModal';
import type {
  MealLogCopyMode,
  MealLogCopyResult,
} from '@/lib/local-api/meal/meal-log-copy';

export interface DispatchMealLogCopyInput {
  /** The logs to operate on. Ids only — see `MealLogCopyModalProps`. */
  sourceIds: string[];
  mode: MealLogCopyMode;
  /** The source day; the destination picker defaults to it. */
  defaultDate: Date;
  /**
   * Optional enrichment for the summary's name lines — whatever `mealsById`
   * map the calling screen already holds. No new fetch, and no `await` on the
   * write path (INV-0013).
   */
  mealNamesById?: Record<string, string>;
}

export interface UseMealLogCopyResult {
  /** Open the destination dialog; the summary follows automatically. */
  dispatchMealLogCopy: (input: DispatchMealLogCopyInput) => void;
}

/**
 * The one place that wires destination modal → local copy/move → summary
 * modal, so all four entry points (single item, hand-picked selection, whole
 * slot, whole day) behave identically instead of each screen re-implementing
 * the chain. Mirrors `useWorkoutDuplicate`.
 *
 * It also owns the **`hasReverted` ref guard**. The summary's own button
 * disables on press, but a ref is what actually makes undo one-shot across
 * renders: a second run would target already-deleted ids for a copy, or
 * re-apply a stale prior state for a move. The latch is set on **success
 * only** — a rejected revert has to stay retryable (DD6).
 */
export const useMealLogCopy = (): UseMealLogCopyResult => {
  const { showModal } = useModal();
  const powerSync = usePowerSync();
  const hasRevertedRef = useRef(false);

  const dispatchMealLogCopy = useCallback(
    ({
      sourceIds,
      mode,
      defaultDate,
      mealNamesById,
    }: DispatchMealLogCopyInput) => {
      hasRevertedRef.current = false;

      const openSummary = (result: MealLogCopyResult) => {
        // `showModal` types its component as `ComponentType<ModalProps>` (an
        // index-signature bag); cast to bridge our specifically-typed modal
        // props — the same rough edge the other dispatch hooks hit.
        showModal(
          MealLogCopySummaryModal as unknown as ComponentType<ModalProps>,
          {
            result,
            mealNamesById,
            onUndo: async () => {
              if (hasRevertedRef.current) return;
              await db.MealLog.mutations.revertCopy(powerSync, result);
              hasRevertedRef.current = true;
            },
          },
        );
      };

      showModal(MealLogCopyModal as unknown as ComponentType<ModalProps>, {
        sourceIds,
        mode,
        defaultDate,
        onDone: openSummary,
      });
    },
    [showModal, powerSync],
  );

  return { dispatchMealLogCopy };
};
